import type { EntityAdapter, EntityState, PayloadAction } from "@reduxjs/toolkit";

// Builds a reducer + action creator that upserts server rows into a slice during
// a pull. Tagged with `meta.fromSync` so the dirty-tracking middleware ignores it
// — otherwise applying pulled rows would re-mark them dirty and push them straight
// back (an echo loop).
export function makeSyncUpsert<T extends { id: string }>(
  adapter: EntityAdapter<T, string>,
) {
  return {
    reducer(state: EntityState<T, string>, action: PayloadAction<T[]>) {
      adapter.upsertMany(state, action.payload);
    },
    prepare(rows: T[]) {
      return { payload: rows, meta: { fromSync: true } };
    },
  };
}

// Builds a reducer + action creator that hard-removes rows from a slice. Used by
// the tombstone purge to drop long-dead soft-deleted rows locally. Tagged
// `meta.fromSync` for consistency with the pull path — though a `removeMany`
// can't re-mark anything dirty anyway (the dirty tracker only diffs rows that
// still exist), so this purely documents that it's sync-layer bookkeeping.
export function makeSyncRemove<T extends { id: string }>(
  adapter: EntityAdapter<T, string>,
) {
  return {
    reducer(state: EntityState<T, string>, action: PayloadAction<string[]>) {
      adapter.removeMany(state, action.payload);
    },
    prepare(ids: string[]) {
      return { payload: ids, meta: { fromSync: true } };
    },
  };
}
